import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Trash2, Lock, Info } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { BasarNav } from "../components/basar/BasarNav";
import { ImageUploadField } from "../components/basar/ImageUploadField";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription } from "../components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { api, errorMessage } from "../lib/api";
import { dashboardBasarUrl, fromLocalInput, nok, toLocalInput, useMyBasar } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

const MAX_SERIES = 10000;
const EMPTY = { name: "", description: "", image_url: "", series_from: "1", series_to: "1000", price: "20", min_per_buyer: "1", max_per_order: "50", sale_start: "", sale_end: "", draw_info: "", contact_info: "", permit_ref: "", show_winner_names: true,
  draw_at: "", draw_place: "", draw_electronic: false, poster_note: "", gift_min_amount: "100" };

const toForm = (b) => ({
  name: b.name || "", description: b.description || "", image_url: b.image_url || "", series_from: String(b.series_from ?? 1), series_to: String(b.series_to ?? 1000), price: String(b.price ?? 20),
  min_per_buyer: String(b.min_per_buyer ?? 1), max_per_order: String(b.max_per_order ?? 50), sale_start: toLocalInput(b.sale_start), sale_end: toLocalInput(b.sale_end), draw_info: b.draw_info || "",
  contact_info: b.contact_info || "", permit_ref: b.permit_ref || "", show_winner_names: b.show_winner_names !== false,
  draw_at: toLocalInput(b.draw_at), draw_place: b.draw_place || "", draw_electronic: !!b.draw_electronic, poster_note: b.poster_note || "", gift_min_amount: String(b.gift_min_amount ?? 100),
});

// /dashboard/basar/ny og /dashboard/basar/:id/oppsett – oppsett av basaren (serie, pris, minstekjøp, salsperiode, info)
export default function BasarEditorPage() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const { basar, loading, error } = useMyBasar(id);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (basar) setForm(toForm(basar));
  }, [basar]);
  useEffect(() => {
    if (error === "notfound") {
      toast.error(t("Fant ikke basaren"));
      navigate("/dashboard/basar");
    }
  }, [error, navigate, t]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (k) => parseInt(form[k], 10);
  const total = Number.isFinite(num("series_from")) && Number.isFinite(num("series_to")) ? num("series_to") - num("series_from") + 1 : 0;
  const sold = basar?.metrics?.sold || 0;
  const locked = sold > 0; // pris + startnummer låst etter første sal
  const drawn = basar?.status === "drawn";
  const canSave = form.name.trim().length >= 2 && total >= 1 && total <= MAX_SERIES && num("price") >= 1 && num("min_per_buyer") >= 1 && num("max_per_order") >= num("min_per_buyer") && !drawn;

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) {
      toast.error(t("Sjekk feltene – navn, nummerserie og pris må fylles ut"));
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(), description: form.description.trim(), image_url: form.image_url, series_from: num("series_from"), series_to: num("series_to"), price: num("price"),
      min_per_buyer: num("min_per_buyer"), max_per_order: num("max_per_order"), sale_start: fromLocalInput(form.sale_start), sale_end: fromLocalInput(form.sale_end),
      draw_info: form.draw_info.trim(), contact_info: form.contact_info.trim(), permit_ref: form.permit_ref.trim(), show_winner_names: form.show_winner_names,
      draw_at: fromLocalInput(form.draw_at), draw_place: form.draw_place.trim(), draw_electronic: form.draw_electronic, poster_note: form.poster_note.trim(),
      gift_min_amount: Math.min(100000, Math.max(10, parseInt(form.gift_min_amount, 10) || 100)),
    };
    try {
      if (isNew) {
        const { data } = await api.post("/my/basar", payload);
        toast.success(t("Basaren er opprettet – legg til premiene"));
        navigate(dashboardBasarUrl(data.basar.id, "premiar"));
      } else {
        await api.put(`/my/basar/${id}`, payload);
        toast.success(t("Basaren er lagret"));
        navigate(dashboardBasarUrl(id));
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/my/basar/${id}`);
      toast.success(t("Basaren er slettet"));
      navigate("/dashboard/basar");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="basar-editor-page">
        <BasarNav basar={isNew ? null : basar} />
        {isNew && (
          <Button asChild variant="ghost" size="sm" data-testid="basar-editor-back"><Link to="/dashboard/basar"><ArrowLeft className="w-4 h-4 mr-1.5" /> {t("Alle basarer")}</Link></Button>
        )}
        {!isNew && loading ? (
          <div className="grid gap-6 lg:grid-cols-12"><Skeleton className="h-[600px] rounded-xl lg:col-span-8" /><Skeleton className="h-72 rounded-xl lg:col-span-4" /></div>
        ) : (
          <form onSubmit={save} className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start" data-testid="basar-editor-form">
            <Card className="border-border lg:col-span-8">
              <CardContent className="p-5 sm:p-6 space-y-7">
                <div className="space-y-1">
                  <h2 className="text-lg font-heading font-semibold tracking-tight">{isNew ? t("Ny basar") : t("Oppsett")}</h2>
                  <p className="text-sm text-muted-foreground">{t("Navn, nummerserie, pris og regler for kjøp. Premiene legger du til i neste steg.")}</p>
                </div>
                {drawn && <Alert data-testid="basar-editor-drawn-lock"><Lock className="h-4 w-4" /><AlertDescription>{t("Basaren er trukket og kan ikke endres.")}</AlertDescription></Alert>}

                <div className="space-y-1.5">
                  <Label htmlFor="be-name">{t("Navn på basaren")}</Label>
                  <Input id="be-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} required placeholder={t("F.eks. Vårbasaren 2026")} disabled={drawn} data-testid="basar-editor-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="be-desc">{t("Beskrivelse")}</Label>
                  <Textarea id="be-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} maxLength={3000} placeholder={t("Hva går overskuddet til? Når og hvor trekkes det?")} disabled={drawn} data-testid="basar-editor-description-textarea" />
                </div>
                <div className="space-y-2">
                  <Label>{t("Bilde (valgfritt)")}</Label>
                  <ImageUploadField value={form.image_url} onChange={(url) => set("image_url", url)} aspect="aspect-[21/9]" size="w-full sm:w-72" testId="basar-editor-image" disabled={drawn} hint={t("Brukes som toppbilde på basarsiden og i oversikten. Liggende format fungerer best.")} />
                </div>

                {/* Nummerserie + pris */}
                <div className="space-y-3">
                  <div>
                    <h3 className="font-heading font-semibold">{t("Nummerserie og pris")}</h3>
                    {locked && <p className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5"><Lock className="w-3 h-3" /> {t("Det er solgt lodd: pris og startnummer er låst, serien kan bare utvides.")}</p>}
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="be-from">{t("Fra nummer")}</Label>
                      <Input id="be-from" type="number" min={1} inputMode="numeric" value={form.series_from} onChange={(e) => set("series_from", e.target.value)} disabled={locked || drawn} data-testid="basar-editor-series-from-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="be-to">{t("Til nummer")}</Label>
                      <Input id="be-to" type="number" min={1} inputMode="numeric" value={form.series_to} onChange={(e) => set("series_to", e.target.value)} disabled={drawn} data-testid="basar-editor-series-to-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="be-price">{t("Pris per lodd (kr)")}</Label>
                      <Input id="be-price" type="number" min={1} max={100000} inputMode="numeric" value={form.price} onChange={(e) => set("price", e.target.value)} disabled={locked || drawn} data-testid="basar-editor-price-input" />
                    </div>
                  </div>
                  <p className={`text-xs tabular-nums ${total > MAX_SERIES || total < 1 ? "text-[hsl(var(--danger))]" : "text-muted-foreground"}`} data-testid="basar-editor-series-summary">
                    {total > MAX_SERIES ? t("Maks {n} lodd i én serie", { n: MAX_SERIES }) : total < 1 ? t("Serien må gå fra et lavere til et høyere nummer") : t("{total} lodd · fullt salg gir {amount}", { total, amount: nok(total * (num("price") || 0)) })}
                  </p>
                </div>

                {/* Kjøpsreglar */}
                <div className="space-y-3">
                  <h3 className="font-heading font-semibold">{t("Regler for kjøp")}</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="be-min">{t("Minste antall lodd per kjøp")}</Label>
                      <Input id="be-min" type="number" min={1} max={100} inputMode="numeric" value={form.min_per_buyer} onChange={(e) => set("min_per_buyer", e.target.value)} disabled={drawn} data-testid="basar-editor-min-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="be-max">{t("Maks antall lodd per kjøp")}</Label>
                      <Input id="be-max" type="number" min={1} max={500} inputMode="numeric" value={form.max_per_order} onChange={(e) => set("max_per_order", e.target.value)} disabled={drawn} data-testid="basar-editor-max-input" />
                    </div>
                  </div>
                </div>

                {/* Salsperiode */}
                <div className="space-y-3">
                  <div>
                    <h3 className="font-heading font-semibold">{t("Salgsperiode (valgfritt)")}</h3>
                    <p className="text-xs text-muted-foreground">{t("Tomme felt = salget er åpent så lenge basaren har status Åpen.")}</p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="be-start">{t("Salgsstart")}</Label>
                      <Input id="be-start" type="datetime-local" value={form.sale_start} onChange={(e) => set("sale_start", e.target.value)} disabled={drawn} data-testid="basar-editor-sale-start-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="be-end">{t("Salgsslutt")}</Label>
                      <Input id="be-end" type="datetime-local" value={form.sale_end} onChange={(e) => set("sale_end", e.target.value)} disabled={drawn} data-testid="basar-editor-sale-end-input" />
                    </div>
                  </div>
                </div>

                {/* Trekning og plakat (Phase 78) */}
                <div className="space-y-3" data-testid="basar-editor-draw-section">
                  <div>
                    <h3 className="font-heading font-semibold">{t("Trekning og plakat")}</h3>
                    <p className="text-xs text-muted-foreground">{t("Vises på basarsiden og på premieplakaten (A4/A3) du kan skrive ut under Gaver.")}</p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="be-draw-at">{t("Dato og klokkeslett for trekningen")}</Label>
                      <Input id="be-draw-at" type="datetime-local" value={form.draw_at} onChange={(e) => set("draw_at", e.target.value)} disabled={drawn} data-testid="basar-editor-draw-at-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="be-draw-place">{t("Sted for trekningen")}</Label>
                      <Input id="be-draw-place" value={form.draw_place} onChange={(e) => set("draw_place", e.target.value)} maxLength={160} placeholder={t("F.eks. Sæbøvik samfunnshus")} disabled={drawn || form.draw_electronic} data-testid="basar-editor-draw-place-input" />
                    </div>
                  </div>
                  <label className="flex items-start gap-3 rounded-lg border border-border px-3 py-3 cursor-pointer">
                    <Switch checked={form.draw_electronic} onCheckedChange={(v) => set("draw_electronic", v)} disabled={drawn} data-testid="basar-editor-electronic-switch" />
                    <span className="text-sm">
                      <span className="block font-medium">{t("Ren elektronisk trekning (uten arrangement)")}</span>
                      <span className="block text-xs text-muted-foreground">{form.draw_electronic ? t("Stedet vises ikke på plakaten eller basarsiden. Vinnerne publiseres på basarsiden.") : t("Slå på hvis trekningen bare skjer digitalt – da skjules stedet på plakaten.")}</span>
                    </span>
                  </label>
                  <div className="space-y-1.5">
                    <Label htmlFor="be-poster-note">{t("Merknad på plakaten (valgfritt)")}</Label>
                    <Textarea id="be-poster-note" value={form.poster_note} onChange={(e) => set("poster_note", e.target.value)} rows={2} maxLength={300} placeholder={t("F.eks. Kaffe og kaker serveres")} disabled={drawn} data-testid="basar-editor-poster-note-input" />
                    <p className="text-xs text-muted-foreground">{t("Vises bare når feltet er fylt ut.")}</p>
                  </div>
                </div>

                {/* Gi premie (Phase 78) */}
                <div className="space-y-3" data-testid="basar-editor-gift-section">
                  <div>
                    <h3 className="font-heading font-semibold">{t("Gi premie")}</h3>
                    <p className="text-xs text-muted-foreground">{t("Publikum kan gi pengegaver (Vipps) og gjenstander som premier fra basarsiden. Gavene legges automatisk inn i premielisten og gaveregisteret.")}</p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="be-gift-min">{t("Minste pengegave (kr)")}</Label>
                      <Input id="be-gift-min" type="number" min={10} max={100000} inputMode="numeric" value={form.gift_min_amount} onChange={(e) => set("gift_min_amount", e.target.value)} disabled={drawn} data-testid="basar-editor-gift-min-input" />
                      <p className="text-xs text-muted-foreground">{t("Standard 100 kr. Beløpet blir en egen pengepremie.")}</p>
                    </div>
                  </div>
                </div>

                {/* Info til publikum */}
                <div className="space-y-3">
                  <h3 className="font-heading font-semibold">{t("Informasjon til publikum")}</h3>
                  <div className="space-y-1.5">
                    <Label htmlFor="be-draw">{t("Annen informasjon om trekningen (valgfritt)")}</Label>
                    <Input id="be-draw" value={form.draw_info} onChange={(e) => set("draw_info", e.target.value)} maxLength={200} placeholder={t("F.eks. Trekning etter 17. mai-toget, vinnerne publiseres her")} disabled={drawn} data-testid="basar-editor-draw-info-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="be-contact">{t("Kontakt for kjøpere og vinnere")}</Label>
                    <Input id="be-contact" value={form.contact_info} onChange={(e) => set("contact_info", e.target.value)} maxLength={300} placeholder={t("F.eks. basar@laget.no / 900 00 000")} disabled={drawn} data-testid="basar-editor-contact-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="be-permit">{t("Lotteritillatelse / referanse (valgfritt)")}</Label>
                    <Input id="be-permit" value={form.permit_ref} onChange={(e) => set("permit_ref", e.target.value)} maxLength={120} disabled={drawn} data-testid="basar-editor-permit-input" />
                    <p className="text-xs text-muted-foreground">{t("Arrangøren er selv ansvarlig for at basaren er lovlig (Lotteritilsynet). Referansen vises på basarsiden.")}</p>
                  </div>
                  <label className="flex items-start gap-3 rounded-lg border border-border px-3 py-3 cursor-pointer">
                    <Switch checked={form.show_winner_names} onCheckedChange={(v) => set("show_winner_names", v)} disabled={drawn} data-testid="basar-editor-show-names-switch" />
                    <span className="text-sm">
                      <span className="block font-medium">{t("Vis vinnernes navn i den offentlige vinnerlisten")}</span>
                      <span className="block text-xs text-muted-foreground">{form.show_winner_names ? t("Loddnummer og fullt navn vises på basarsiden etter trekningen.") : t("Bare loddnummer vises offentlig – du ser navn og mobil i dashbordet.")}</span>
                    </span>
                  </label>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                  <Button type="submit" disabled={!canSave || saving} data-testid="basar-editor-save-button">
                    {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} {saving ? t("Lagrer …") : isNew ? t("Opprett basar") : t("Lagre")}
                  </Button>
                  {!isNew && !drawn && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button type="button" variant="ghost" className="text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" disabled={locked} title={locked ? t("Basaren har solgte lodd og kan ikke slettes – steng den i stedet") : undefined} data-testid="basar-editor-delete-button"><Trash2 className="w-4 h-4 mr-1.5" /> {t("Slett")}</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("Slette basaren?")}</AlertDialogTitle>
                          <AlertDialogDescription>{t("Basaren og premiene fjernes. Dette kan ikke angres.")}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="basar-editor-delete-cancel">{t("Avbryt")}</AlertDialogCancel>
                          <AlertDialogAction onClick={remove} className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:opacity-95" data-testid="basar-editor-delete-confirm">{t("Ja, slett")}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Hjelpekolonne */}
            <div className="lg:col-span-4 space-y-4 lg:sticky lg:top-24">
              <Card className="border-border" data-testid="basar-editor-steps">
                <CardContent className="p-5 space-y-3 text-sm">
                  <p className="font-heading font-semibold">{t("Slik kommer du i gang")}</p>
                  <ol className="space-y-2 text-muted-foreground list-none">
                    <li className="flex gap-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] text-xs font-semibold shrink-0">1</span><span><span className="text-foreground font-medium">{t("Oppsett")}</span> – {t("navn, nummerserie, pris og regler.")}</span></li>
                    <li className="flex gap-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] text-xs font-semibold shrink-0">2</span><span><span className="text-foreground font-medium">{t("Premier")}</span> – {t("bilde, verdi og antall eksemplarer.")}</span></li>
                    <li className="flex gap-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] text-xs font-semibold shrink-0">3</span><span><span className="text-foreground font-medium">{t("Åpne salget")}</span> – {t("del lenken, registrer salg ved døren.")}</span></li>
                    <li className="flex gap-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] text-xs font-semibold shrink-0">4</span><span><span className="text-foreground font-medium">{t("Trekning")}</span> – {t("trekk vinnere og publiser listen.")}</span></li>
                  </ol>
                </CardContent>
              </Card>
              <Alert data-testid="basar-editor-settlement-note">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">{t("Betalingene går direkte til deres egen Vipps-salgsenhet. Right in tar ingen avgift på loddsalg og formidler bare kjøpet.")}</AlertDescription>
              </Alert>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
